import { useEffect, useState, useRef } from "react";
import { fetchCompany, updateCompany } from "@/lib/api";
import { useCompany } from "@/lib/CompanyContext";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Buildings, Image as ImageIcon, Trash, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_LOGO_SIZE_KB = 500;
const empty = { nome: "", slogan: "", cnpj: "", endereco: "", telefone: "", email: "", logo_base64: "" };

export default function Company() {
  const { refresh } = useCompany();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { fetchCompany().then((d) => setForm({ ...empty, ...d })); }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_LOGO_SIZE_KB * 1024) {
      toast.error(`Imagem maior que ${MAX_LOGO_SIZE_KB}KB. Reduza o arquivo.`);
      e.target.value = "";
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Arquivo deve ser uma imagem (PNG, JPG ou SVG).");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => setField("logo_base64", ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const onRemoveLogo = () => setField("logo_base64", "");

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCompany(form);
      toast.success("Identidade da empresa salva");
      refresh();
    } catch {
      toast.error("Erro ao salvar");
    }
    setSaving(false);
  };

  return (
    <div data-testid="company-page">
      <PageHeader
        title="Identidade da Empresa"
        subtitle="Personalize logotipo, dados e contatos da sua oficina. Aparecem no sidebar e no PDF da Ordem de Serviço."
      />

      <form onSubmit={onSave} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo upload */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-[#E2E8F0] rounded-md p-6">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-3">
              <ImageIcon size={14} weight="duotone" /> Logotipo
            </div>

            <div className="aspect-square bg-[#F8FAFC] border border-dashed border-[#E2E8F0] rounded-md flex items-center justify-center overflow-hidden mb-4">
              {form.logo_base64 ? (
                <img
                  src={form.logo_base64}
                  alt="Logo"
                  className="max-w-full max-h-full object-contain p-4"
                  data-testid="logo-preview"
                />
              ) : (
                <div className="text-center text-[#475569] text-sm p-6">
                  <Buildings size={36} weight="duotone" className="mx-auto mb-2 text-[#CBD5E1]" />
                  <div>Nenhum logo enviado</div>
                  <div className="text-xs mt-1">PNG, JPG ou SVG · até {MAX_LOGO_SIZE_KB}KB</div>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={onFile}
                className="hidden"
                data-testid="logo-input"
              />
              <Button type="button" onClick={() => fileRef.current?.click()} className="bg-[#001E50] hover:bg-[#001740] text-white w-full">
                <UploadSimple size={16} weight="bold" className="mr-1.5" />
                {form.logo_base64 ? "Trocar logotipo" : "Enviar logotipo"}
              </Button>
              {form.logo_base64 && (
                <Button type="button" variant="outline" onClick={onRemoveLogo} data-testid="remove-logo-btn">
                  <Trash size={16} className="mr-1.5 text-red-500" />
                  Remover
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Form fields */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-[#E2E8F0] rounded-md p-6 space-y-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569]">Identidade</div>

            <div>
              <Label>Nome da empresa *</Label>
              <Input
                data-testid="company-nome"
                placeholder="Ex: Oficina Mecânica Silva"
                value={form.nome}
                onChange={(e) => setField("nome", e.target.value)}
              />
              <p className="text-xs text-[#475569] mt-1">Exibido no menu lateral e no cabeçalho do PDF.</p>
            </div>

            <div>
              <Label>Slogan / subtítulo</Label>
              <Input
                data-testid="company-slogan"
                placeholder="Ex: Serviços automotivos desde 1995"
                value={form.slogan}
                onChange={(e) => setField("slogan", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>CNPJ</Label>
                <Input
                  data-testid="company-cnpj"
                  placeholder="00.000.000/0000-00"
                  value={form.cnpj}
                  onChange={(e) => setField("cnpj", e.target.value)}
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input
                  data-testid="company-telefone"
                  placeholder="(11) 9 9999-9999"
                  value={form.telefone}
                  onChange={(e) => setField("telefone", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Endereço completo</Label>
                <Textarea
                  data-testid="company-endereco"
                  placeholder="Rua, número, bairro, cidade/UF, CEP"
                  rows={2}
                  value={form.endereco}
                  onChange={(e) => setField("endereco", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label>E-mail de contato</Label>
                <Input
                  data-testid="company-email"
                  type="email"
                  placeholder="contato@oficina.com"
                  value={form.email}
                  onChange={(e) => setField("email", e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                type="submit"
                data-testid="save-company-btn"
                disabled={saving}
                className="bg-[#001E50] hover:bg-[#001740] text-white"
              >
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-md p-5 mt-4 text-sm text-[#475569]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mb-2">Pré-visualização do PDF</div>
            <div className="border border-[#E2E8F0] rounded p-4 flex items-center gap-4 bg-[#F8FAFC]">
              {form.logo_base64 ? (
                <img src={form.logo_base64} alt="" className="w-16 h-16 object-contain" />
              ) : (
                <div className="w-16 h-16 bg-[#CBD5E1] rounded flex items-center justify-center text-white font-heading text-xl">
                  {form.nome ? form.nome[0]?.toUpperCase() : "?"}
                </div>
              )}
              <div>
                <div className="font-heading font-bold text-[#001E50] text-lg">{form.nome || "Nome da Empresa"}</div>
                {form.slogan && <div className="text-xs text-[#475569]">{form.slogan}</div>}
                {form.cnpj && <div className="text-xs text-[#475569] mt-0.5">CNPJ: {form.cnpj}</div>}
                {form.endereco && <div className="text-xs text-[#475569]">{form.endereco}</div>}
                {(form.telefone || form.email) && (
                  <div className="text-xs text-[#475569] mt-0.5">
                    {[form.telefone, form.email].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
